import { useState } from 'react';
import { useToast } from '../../../../../shared/hooks/use-toast';
import { apiErrorMessage } from '../../../../../shared/policies/api-error-message';
import { workOrderNumber } from '../policies/work-order-labels';
import { moveWorkOrder, type WorkOrderMove, type WorkOrderView } from '../work-orders.api';

const DONE: Record<WorkOrderMove, string> = {
  approve: 'approved',
  reject: 'rejected',
  schedule: 'scheduled',
  start: 'started',
  complete: 'marked complete',
  cancel: 'cancelled',
};

/** Takes an order through a move. A refusal stays inline on the page, where the buttons are; a move that went through toasts and reloads the order. */
export function useWorkOrderMoves(order: WorkOrderView, reload: () => Promise<void>) {
  const [error, setError] = useState<string | null>(null);
  const [moving, setMoving] = useState<WorkOrderMove | null>(null);
  const toast = useToast();

  async function perform(move: WorkOrderMove, body?: Record<string, string>): Promise<boolean> {
    setError(null);
    setMoving(move);
    try {
      await moveWorkOrder(order.id, move, body);
      await reload();
      toast.success(`Work order ${workOrderNumber(order.sequenceNumber)} ${DONE[move]}`);
      return true;
    } catch (err) {
      setError(apiErrorMessage(err) ?? `Could not ${move} the work order — try again.`);
      return false;
    } finally {
      setMoving(null);
    }
  }

  return { perform, error, moving, clearError: () => setError(null) };
}
